import React, { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { Lock, Bell, Database, AlertTriangle, Shield, Globe, Accessibility } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AccountSettings {
  email_notifications: boolean;
  push_notifications: boolean;
  privacy_settings: {
    profile_visibility: 'public' | 'private';
    show_email: boolean;
    show_location: boolean;
    show_social_links: boolean;
    allow_mentions: boolean;
    allow_messages: boolean;
    show_age: boolean;
    show_online_status: boolean;
    allow_friend_requests: boolean;
    allow_group_invites: boolean;
    block_explicit_content: boolean;
    two_factor_auth: boolean;
  };
  backup_frequency: 'never' | 'daily' | 'weekly' | 'monthly';
  analytics_enabled: boolean;
  language: string;
  timezone: string;
  date_format: '12h' | '24h';
  accessibility: {
    high_contrast: boolean;
    reduce_motion: boolean;
    larger_text: boolean;
  };
}

export function AccountSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<AccountSettings>({
    email_notifications: true,
    push_notifications: true,
    privacy_settings: {
      profile_visibility: 'public',
      show_email: false,
      show_location: true,
      show_social_links: true,
      allow_mentions: true,
      allow_messages: true,
      show_age: false,
      show_online_status: true,
      allow_friend_requests: true,
      allow_group_invites: true,
      block_explicit_content: true,
      two_factor_auth: false
    },
    backup_frequency: 'never',
    analytics_enabled: true,
    language: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    date_format: '12h',
    accessibility: {
      high_contrast: false,
      reduce_motion: false,
      larger_text: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      if (profile) {
        setSettings({
          email_notifications: profile.email_notifications,
          push_notifications: profile.push_notifications,
          privacy_settings: profile.privacy_settings,
          backup_frequency: profile.backup_frequency,
          analytics_enabled: profile.analytics_enabled,
          language: profile.language || 'en',
          timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          date_format: profile.date_format || '12h',
          accessibility: profile.accessibility || {
            high_contrast: false,
            reduce_motion: false,
            larger_text: false
          }
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load account settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          email_notifications: settings.email_notifications,
          push_notifications: settings.push_notifications,
          privacy_settings: settings.privacy_settings,
          backup_frequency: settings.backup_frequency,
          analytics_enabled: settings.analytics_enabled,
          language: settings.language,
          timezone: settings.timezone,
          date_format: settings.date_format,
          accessibility: settings.accessibility
        })
        .eq('id', user?.id);

      if (error) throw error;
      setSuccess(true);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="bg-white dark:bg-dark-paper rounded-xl shadow-sm">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Account Settings</h2>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
              Settings saved successfully!
            </div>
          )}

          <div className="space-y-6">
            {/* Notifications */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.email_notifications}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      email_notifications: e.target.checked
                    }))}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Email Notifications</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.push_notifications}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      push_notifications: e.target.checked
                    }))}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Push Notifications</span>
                </label>
              </div>
            </div>

            {/* Privacy Settings */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Privacy</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Profile Visibility
                </label>
                <select
                  value={settings.privacy_settings.profile_visibility}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    privacy_settings: {
                      ...prev.privacy_settings,
                      profile_visibility: e.target.value as 'public' | 'private'
                    }
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>

              <div className="space-y-2">
                {[
                  { key: 'show_email', label: 'Show Email' },
                  { key: 'show_location', label: 'Show Location' },
                  { key: 'show_social_links', label: 'Show Social Links' },
                  { key: 'allow_mentions', label: 'Allow Mentions' },
                  { key: 'allow_messages', label: 'Allow Messages' },
                  { key: 'show_age', label: 'Show Age' },
                  { key: 'show_online_status', label: 'Show Online Status' },
                  { key: 'allow_friend_requests', label: 'Allow Friend Requests' },
                  { key: 'allow_group_invites', label: 'Allow Group Invites' },
                  { key: 'block_explicit_content', label: 'Block Explicit Content' },
                  { key: 'two_factor_auth', label: 'Two-Factor Authentication' }
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.privacy_settings[key as keyof typeof settings.privacy_settings]}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        privacy_settings: {
                          ...prev.privacy_settings,
                          [key]: e.target.checked
                        }
                      }))}
                      className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Language & Region */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Language & Region</h3>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Language
                  </label>
                  <select
                    value={settings.language}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      language: e.target.value
                    }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                  >
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="fr">Français</option>
                    <option value="de">Deutsch</option>
                    <option value="it">Italiano</option>
                    <option value="pt">Português</option>
                    <option value="ja">日本語</option>
                    <option value="ko">한국어</option>
                    <option value="zh">中文</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Time Format
                  </label>
                  <select
                    value={settings.date_format}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      date_format: e.target.value as '12h' | '24h'
                    }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                  >
                    <option value="12h">12-hour (1:00 PM)</option>
                    <option value="24h">24-hour (13:00)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Accessibility */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Accessibility className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Accessibility</h3>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.accessibility.high_contrast}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      accessibility: {
                        ...prev.accessibility,
                        high_contrast: e.target.checked
                      }
                    }))}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">High Contrast Mode</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.accessibility.reduce_motion}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      accessibility: {
                        ...prev.accessibility,
                        reduce_motion: e.target.checked
                      }
                    }))}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Reduce Motion</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.accessibility.larger_text}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      accessibility: {
                        ...prev.accessibility,
                        larger_text: e.target.checked
                      }
                    }))}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Larger Text</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-dark-elevated border-t dark:border-dark-border">
          <button
            onClick={handleSave}
            disabled={saving}
            className={cn(
              "px-4 py-2 rounded-lg font-medium transition-colors",
              saving
                ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                : "bg-royal-purple text-white hover:bg-royal-purple/90"
            )}
          >
            {saving ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </div>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}